import React, { useState } from 'react';
import axios from 'axios';
import './ImageTransformProcessor.css';

const FIREBASE_FUNCTION_URL = 'https://process-homework2-72eav6542q-uc.a.run.app';

interface ProcessResult {
  success: boolean;
  processed_image?: string;
  error?: string;
  operation?: string;
  original_size?: string;
  result_size?: string;
}

const ImageTransformProcessor: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<string>('');
  const [processedDimensions, setProcessedDimensions] = useState<string>('');

  // Geometric operation states
  const [reflectDirection, setReflectDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [resizeScale, setResizeScale] = useState<string>('0.5');
  const [cropX, setCropX] = useState<string>('50');
  const [cropY, setCropY] = useState<string>('50');
  const [cropWidth, setCropWidth] = useState<string>('200');
  const [cropHeight, setCropHeight] = useState<string>('200');
  const [shiftX, setShiftX] = useState<string>('50');
  const [shiftY, setShiftY] = useState<string>('30');
  const [showSampleModal, setShowSampleModal] = useState(false);

  // Sample images (local files in public folder)
  const sampleImages = [
    'https://upload.wikimedia.org/wikipedia/en/7/7d/Lenna_%28test_image%29.png',
    '/sample-images/VK1.jpg',
    '/sample-images/VK2.jpg',
    '/sample-images/diyetika.png',
    '/sample-images/ailab_logo.png',
    '/sample-images/ikc.png',
  ];


  const handleSampleImageSelect = async (url: string) => {
    try {
      setShowSampleModal(false);
      setError(null);

      // Fetch image and convert to base64
      const response = await fetch(url);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setOriginalDimensions(`${img.width} × ${img.height}`);
        };
        img.src = e.target?.result as string;

        setSelectedImage(e.target?.result as string);
        setProcessedImage(null);
        setProcessedDimensions('');
        setSuccessMessage('Sample image loaded successfully!');
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      setError('Failed to load sample image. Please try uploading your own image.');
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setOriginalDimensions(`${img.width} × ${img.height}`);
        };
        img.src = e.target?.result as string;

        setSelectedImage(e.target?.result as string);
        setProcessedImage(null);
        setProcessedDimensions('');
        setError(null);
        setSuccessMessage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = async (operation: string, params: any = {}) => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await axios.post<ProcessResult>(
        FIREBASE_FUNCTION_URL,
        {
          image: selectedImage,
          operation: operation,
          ...params
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 30000,
        }
      );

      if (response.data.success && response.data.processed_image) {
        const img = new Image();
        img.onload = () => {
          setProcessedDimensions(`${img.width} × ${img.height}`);
        };
        img.src = response.data.processed_image;

        setProcessedImage(response.data.processed_image);
        setSuccessMessage(`${operation} completed successfully!`);
      } else {
        setError(response.data.error || 'Processing failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  // Operation handlers
  const handleReflect = () => {
    processImage('reflect', { direction: reflectDirection });
  };

  const handleResize = () => {
    const scale = parseFloat(resizeScale);
    if (isNaN(scale) || scale <= 0) {
      setError('Invalid scale value');
      return;
    }
    processImage('resize', { scale });
  };

  const handleCrop = () => {
    const x = parseInt(cropX);
    const y = parseInt(cropY);
    const width = parseInt(cropWidth);
    const height = parseInt(cropHeight);

    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
      setError('Invalid crop parameters');
      return;
    }

    processImage('crop', { x, y, width, height });
  };

  const handleShift = () => {
    const sx = parseInt(shiftX);
    const sy = parseInt(shiftY);

    if (isNaN(sx) || isNaN(sy)) {
      setError('Invalid shift parameters');
      return;
    }

    processImage('shift', { shiftX: sx, shiftY: sy });
  };

  // Color space conversion handlers
  const handleColorConversion = (operation: string) => {
    processImage(operation);
  };

  const handleUseProcessed = () => {
    if (processedImage) {
      setSelectedImage(processedImage);
      setOriginalDimensions(processedDimensions);
      setProcessedImage(null);
      setProcessedDimensions('');
      setSuccessMessage('Processed image is now the input image');
    }
  };

  const downloadImage = () => {
    if (processedImage) {
      const link = document.createElement('a');
      link.href = processedImage;
      link.download = 'processed_image.png';
      link.click();
    }
  };

  return (
    <div className="homework2-container">
      <div className="upload-section">
        <div className="upload-buttons">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            id="file-upload"
            className="file-input"
          />
          <label htmlFor="file-upload" className="file-label">
            📁 Upload Image
          </label>
          <button
            onClick={() => setShowSampleModal(true)}
            className="file-label sample-button"
          >
            🖼️ Sample Images
          </button>
        </div>
      </div>

      {/* Sample Images Modal */}
      {showSampleModal && (
        <div className="modal-overlay" onClick={() => setShowSampleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select a Sample Image</h2>
              <button
                className="modal-close"
                onClick={() => setShowSampleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="sample-images-grid">
              {sampleImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className="sample-image-card"
                  onClick={() => handleSampleImageSelect(imageUrl)}
                >
                  <img
                    src={imageUrl}
                    alt={`Sample ${index + 1}`}
                    className="sample-image-preview"
                    crossOrigin="anonymous"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="message error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="message success-message">
          {successMessage}
        </div>
      )}

      <div className="images-container">
        <div className="image-box">
          <h3>Original Image</h3>
          {selectedImage ? (
            <>
              <img src={selectedImage} alt="Original" className="display-image" />
              {originalDimensions && (
                <div className="image-dimensions">
                  {originalDimensions} pixels
                </div>
              )}
            </>
          ) : (
            <div className="placeholder">No image selected</div>
          )}
        </div>

        <div className="image-box">
          <h3>Processed Image</h3>
          {processedImage ? (
            <>
              <img src={processedImage} alt="Processed" className="display-image" />
              {processedDimensions && (
                <div className="image-dimensions">
                  {processedDimensions} pixels
                </div>
              )}
              <div className="image-actions">
                <button onClick={handleUseProcessed} className="btn-secondary">
                  Use as Input
                </button>
                <button onClick={downloadImage} className="btn-secondary">
                  Download
                </button>
              </div>
            </>
          ) : (
            <div className="placeholder">
              {isProcessing ? 'Processing...' : 'Result will appear here'}
            </div>
          )}
        </div>
      </div>

      <div className="controls-container">
        {/* Geometric Operations */}
        <div className="control-section">
          <h2>Geometric Operations</h2>

          <div className="control-group">
            <h4>Reflection</h4>
            <div className="inline-controls">
              <select
                value={reflectDirection}
                onChange={(e) => setReflectDirection(e.target.value as 'horizontal' | 'vertical')}
                className="select-input"
              >
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
              </select>
              <button onClick={handleReflect} disabled={isProcessing} className="btn-primary">
                Reflect
              </button>
            </div>
          </div>

          <div className="control-group">
            <h4>Resize</h4>
            <div className="inline-controls">
              <label>Scale:</label>
              <input
                type="number"
                step="0.1"
                value={resizeScale}
                onChange={(e) => setResizeScale(e.target.value)}
                className="number-input"
              />
              <button onClick={handleResize} disabled={isProcessing} className="btn-primary">
                Resize
              </button>
            </div>
          </div>

          <div className="control-group">
            <h4>Crop</h4>
            <div className="grid-controls">
              <div>
                <label>X:</label>
                <input
                  type="number"
                  value={cropX}
                  onChange={(e) => setCropX(e.target.value)}
                  className="number-input"
                />
              </div>
              <div>
                <label>Y:</label>
                <input
                  type="number"
                  value={cropY}
                  onChange={(e) => setCropY(e.target.value)}
                  className="number-input"
                />
              </div>
              <div>
                <label>Width:</label>
                <input
                  type="number"
                  value={cropWidth}
                  onChange={(e) => setCropWidth(e.target.value)}
                  className="number-input"
                />
              </div>
              <div>
                <label>Height:</label>
                <input
                  type="number"
                  value={cropHeight}
                  onChange={(e) => setCropHeight(e.target.value)}
                  className="number-input"
                />
              </div>
            </div>
            <button onClick={handleCrop} disabled={isProcessing} className="btn-primary full-width">
              Crop
            </button>
          </div>

          <div className="control-group">
            <h4>Shift</h4>
            <div className="inline-controls">
              <label>dX:</label>
              <input
                type="number"
                value={shiftX}
                onChange={(e) => setShiftX(e.target.value)}
                className="number-input"
              />
              <label>dY:</label>
              <input
                type="number"
                value={shiftY}
                onChange={(e) => setShiftY(e.target.value)}
                className="number-input"
              />
              <button onClick={handleShift} disabled={isProcessing} className="btn-primary">
                Shift
              </button>
            </div>
          </div>
        </div>

        {/* Color Space Conversions */}
        <div className="control-section">
          <h2>Color Space Conversions</h2>

          <div className="color-buttons">
            <button
              onClick={() => handleColorConversion('rgb2hsi')}
              disabled={isProcessing}
              className="btn-color"
            >
              RGB → HSI
            </button>
            <button
              onClick={() => handleColorConversion('hsi2rgb')}
              disabled={isProcessing}
              className="btn-color"
            >
              HSI → RGB
            </button>
            <button
              onClick={() => handleColorConversion('rgb2hsv')}
              disabled={isProcessing}
              className="btn-color"
            >
              RGB → HSV
            </button>
            <button
              onClick={() => handleColorConversion('hsv2rgb')}
              disabled={isProcessing}
              className="btn-color"
            >
              HSV → RGB
            </button>
            <button
              onClick={() => handleColorConversion('rgb2yiq')}
              disabled={isProcessing}
              className="btn-color"
            >
              RGB → YIQ
            </button>
            <button
              onClick={() => handleColorConversion('yiq2rgb')}
              disabled={isProcessing}
              className="btn-color"
            >
              YIQ → RGB
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageTransformProcessor;
